import { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { ClickableCard, ActiveFilterChip } from '../components/ClickableCard';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Plus, Edit, Trash2, Eye, Search, Download, Upload, FileSpreadsheet,
  CheckCircle, Clock, AlertTriangle, Loader2, ListChecks, X, Paperclip,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
} from 'lucide-react';
import { toast } from 'sonner';

const TYPES = [
  'Improvement Opportunity', 'Corrective Action', 'Preventive Action',
  'Nonconformity', 'Audit Finding', 'Observation', 'Remediation Action', 'Others',
];
const STATUSES = ['Open', 'In Progress', 'Pending Review', 'Completed', 'Closed', 'Deferred'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const FREQUENCIES = ['One-off', 'Monthly', 'Quarterly', 'Semi-Annual', 'Annual'];
const SOURCES = [
  'Internal Audit', 'External Audit', 'Gap Assessment', 'Risk Assessment',
  'Management Review', 'Compliance Assessment', 'Supplier Review',
  'Policy Review', 'Incident', 'Other',
];
const CLASSIFICATIONS = [
  'Major Nonconformity', 'Minor Nonconformity', 'Observation', 'Opportunity for Improvement',
];
const ROOT_CAUSE_CATEGORIES = ['People', 'Process', 'Technology', 'Supplier', 'Governance', 'Other'];
const EFFECTIVENESS = ['Pass', 'Fail', 'Pending', 'Not Required'];

const emptyForm = {
  title: '', type: 'Corrective Action', status: 'Open', priority: 'Medium',
  source: 'Internal Audit', date_identified: '', finding: '',
  classification: 'Observation', department: '', raised_by: '',
  action_owner: '', date_due: '', root_cause: '', root_cause_category: '',
  action_plan: '', completion_date: '', outcome: '',
  effectiveness_check: '', effectiveness_review_date: '', closure_comments: '',
  frequency: 'One-off',
};

const statusColor = (s) => ({
  'Open': 'bg-slate-100 text-slate-800',
  'In Progress': 'bg-blue-100 text-blue-800',
  'Pending Review': 'bg-amber-100 text-amber-800',
  'Completed': 'bg-emerald-100 text-emerald-800',
  'Closed': 'bg-slate-200 text-slate-900',
  'Deferred': 'bg-rose-100 text-rose-800',
}[s] || 'bg-slate-100 text-slate-700');

const priorityColor = (p) => ({
  'Critical': 'bg-rose-100 text-rose-800',
  'High': 'bg-orange-100 text-orange-800',
  'Medium': 'bg-amber-100 text-amber-800',
  'Low': 'bg-slate-100 text-slate-700',
}[p] || 'bg-slate-100 text-slate-700');

const isOverdue = (a) => a.date_due && !['Completed', 'Closed'].includes(a.status) && new Date(a.date_due) < new Date(new Date().toDateString());

export default function ImprovementActionsPage() {
  const { user, viewingOrg } = useContext(AuthContext);
  const vtq = () => (viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '');

  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [viewingAction, setViewingAction] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [tenantUsers, setTenantUsers] = useState([]);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  // Filters / search / sort / pagination
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [filterStatus, setFilterStatus] = useState(() => searchParams.get('status') || '');
  const [overdueOnly, setOverdueOnly] = useState(() => searchParams.get('overdue') === '1');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortKey, setSortKey] = useState('date_due');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [a, u] = await Promise.all([
          axios.get(`${API}/improvement-actions${vtq()}`),
          axios.get(`${API}/tenant/users${vtq()}`).catch(() => ({ data: [] })),
        ]);
        setActions(a.data || []);
        setTenantUsers(u.data || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load actions');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [refreshKey, viewingOrg?.id]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const openCreate = () => {
    setEditingAction(null);
    setFormData({ ...emptyForm, raised_by: user?.email || '', date_identified: new Date().toISOString().slice(0, 10) });
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditingAction(a);
    setFormData({
      title: a.title || '', type: a.type || 'Corrective Action',
      status: a.status || 'Open', priority: a.priority || 'Medium',
      source: a.source || 'Internal Audit',
      date_identified: a.date_identified?.slice(0, 10) || '',
      finding: a.finding || '', classification: a.classification || 'Observation',
      department: a.department || '', raised_by: a.raised_by || '',
      action_owner: a.action_owner || '', date_due: a.date_due?.slice(0, 10) || '',
      root_cause: a.root_cause || '', root_cause_category: a.root_cause_category || '',
      action_plan: a.action_plan || '',
      completion_date: a.completion_date?.slice(0, 10) || '',
      outcome: a.outcome || '', effectiveness_check: a.effectiveness_check || '',
      effectiveness_review_date: a.effectiveness_review_date?.slice(0, 10) || '',
      closure_comments: a.closure_comments || '',
      frequency: a.frequency || 'One-off',
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return toast.error('Title is required');
    if (!formData.finding.trim()) return toast.error('Finding / Issue is required');
    if (!formData.action_plan.trim()) return toast.error('Action Plan is required');
    if (!formData.action_owner.trim()) return toast.error('Action Owner is required');
    if (!formData.date_due) return toast.error('Date Due is required');
    if (formData.status === 'Closed') {
      if (!formData.completion_date) return toast.error('Completion Date is required to close');
      if (!formData.outcome?.trim()) return toast.error('Outcome / Resolution is required to close');
    }
    setSaving(true);
    try {
      if (editingAction) {
        await axios.patch(`${API}/improvement-actions/${editingAction.id}${vtq()}`, formData);
        toast.success('Action updated');
      } else {
        await axios.post(`${API}/improvement-actions${vtq()}`, formData);
        toast.success('Action created');
      }
      setShowForm(false);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Delete action ${a.action_id}?`)) return;
    try {
      await axios.delete(`${API}/improvement-actions/${a.id}${vtq()}`);
      toast.success('Action deleted');
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/improvement-actions-template`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'action_register_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const downloadReport = async () => {
    try {
      const res = await axios.get(`${API}/improvement-actions-report${vtq()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.download = `Improvement_Actions_Register_${stamp}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download report');
    }
  };

  const doImport = async () => {
    if (!importFile) return toast.error('Select a file first');
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await axios.post(`${API}/improvement-actions/import${vtq()}`, fd);
      setImportResult(res.data);
      toast.success(res.data.message);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // --- Filters / sort / page ---------------------------------------------------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (actions || []).filter((a) => {
      if (q && !(
        (a.action_id || '').toLowerCase().includes(q) ||
        (a.title || '').toLowerCase().includes(q) ||
        (a.finding || '').toLowerCase().includes(q) ||
        (a.action_owner || '').toLowerCase().includes(q)
      )) return false;
      if (filterStatus && a.status !== filterStatus) return false;
      if (overdueOnly && !isOverdue(a)) return false;
      if (filterPriority && a.priority !== filterPriority) return false;
      if (filterType && a.type !== filterType) return false;
      if (filterSource && a.source !== filterSource) return false;
      if (filterOwner && a.action_owner !== filterOwner) return false;
      if (filterDepartment && (a.department || '') !== filterDepartment) return false;
      if (dateFrom && (!a.date_due || a.date_due < dateFrom)) return false;
      if (dateTo && (!a.date_due || a.date_due > dateTo)) return false;
      return true;
    });
  }, [actions, search, filterStatus, overdueOnly, filterPriority, filterType, filterSource, filterOwner, filterDepartment, dateFrom, dateTo]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((x, y) => {
      const a = (x[sortKey] || '').toString();
      const b = (y[sortKey] || '').toString();
      return sortDir === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pagedRows = sorted.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const owners = useMemo(() => Array.from(new Set(actions.map((a) => a.action_owner).filter(Boolean))).sort(), [actions]);
  const departments = useMemo(() => Array.from(new Set(actions.map((a) => a.department).filter(Boolean))).sort(), [actions]);

  const stats = useMemo(() => ({
    total: actions.length,
    open: actions.filter((a) => a.status === 'Open').length,
    inProgress: actions.filter((a) => a.status === 'In Progress').length,
    pendingReview: actions.filter((a) => a.status === 'Pending Review').length,
    overdue: actions.filter(isOverdue).length,
    completed: actions.filter((a) => a.status === 'Completed').length,
    closed: actions.filter((a) => a.status === 'Closed').length,
  }), [actions]);

  // --- Evidence (on viewingAction) --------------------------------------------
  const uploadEvidence = async (file) => {
    if (!viewingAction) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/improvement-actions/${viewingAction.id}/evidence${vtq()}`, fd);
      const updated = { ...viewingAction, evidence: [...(viewingAction.evidence || []), res.data] };
      setViewingAction(updated);
      setActions((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      toast.success('Evidence uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
  };

  const downloadEvidence = async (ev) => {
    try {
      const sep = vtq() ? '&' : '?';
      const url = `${API}/improvement-actions/${viewingAction.id}/evidence/${ev.id}${vtq()}${sep}_t=${Date.now()}`;
      const res = await axios.get(url, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(res.data);
      a.download = ev.filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error('Download failed');
    }
  };

  const deleteEvidence = async (ev) => {
    if (!window.confirm(`Remove ${ev.filename}?`)) return;
    try {
      await axios.delete(`${API}/improvement-actions/${viewingAction.id}/evidence/${ev.id}${vtq()}`);
      const updated = { ...viewingAction, evidence: viewingAction.evidence.filter((e) => e.id !== ev.id) };
      setViewingAction(updated);
      setActions((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      toast.success('Evidence removed');
    } catch {
      toast.error('Delete failed');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="improvement-actions-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Improvement &amp; Actions Register</h1>
          <p className="text-sm text-slate-600 mt-1">Track corrective actions, audit findings, observations and remediation activities.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadTemplate} data-testid="dl-template">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Template
          </Button>
          <Button variant="outline" onClick={() => { setShowImport(true); setImportResult(null); setImportFile(null); }} data-testid="open-import">
            <Upload className="w-4 h-4 mr-1" /> Import
          </Button>
          <Button variant="outline" onClick={downloadReport} data-testid="dl-report">
            <Download className="w-4 h-4 mr-1" /> Download Report
          </Button>
          <Button className="bg-slate-900 text-white hover:bg-slate-800" onClick={openCreate} data-testid="add-action-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Action
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { label: 'Total', val: stats.total, Icon: ListChecks, color: 'text-slate-700', status: '' },
          { label: 'Open', val: stats.open, Icon: Clock, color: 'text-slate-700', status: 'Open' },
          { label: 'In Progress', val: stats.inProgress, Icon: Loader2, color: 'text-blue-700', status: 'In Progress' },
          { label: 'Pending Review', val: stats.pendingReview, Icon: Eye, color: 'text-amber-700', status: 'Pending Review' },
          { label: 'Overdue', val: stats.overdue, Icon: AlertTriangle, color: 'text-rose-700', overdue: true },
          { label: 'Completed', val: stats.completed, Icon: CheckCircle, color: 'text-emerald-700', status: 'Completed' },
          { label: 'Closed', val: stats.closed, Icon: CheckCircle, color: 'text-slate-700', status: 'Closed' },
        ].map((s) => {
          const slugged = s.label.toLowerCase().replace(/ /g, '-');
          const active = s.overdue ? overdueOnly : (!overdueOnly && filterStatus === s.status);
          const onClick = () => {
            if (s.overdue) { setOverdueOnly(!overdueOnly); setFilterStatus(''); return; }
            setOverdueOnly(false);
            setFilterStatus(active ? '' : s.status);
          };
          return (
            <ClickableCard key={s.label} active={active} onClick={onClick} testid={`stat-${slugged}-card`}>
              <Card className="p-4 bg-white h-full">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-600 font-medium">{s.label}</p>
                  <s.Icon className={`w-4 h-4 ${s.color}`} />
                </div>
                <p className="text-2xl font-heading font-semibold text-slate-900 mt-1" data-testid={`stat-${slugged}`}>{s.val}</p>
              </Card>
            </ClickableCard>
          );
        })}
      </div>

      {overdueOnly && (
        <ActiveFilterChip label={`Overdue (${filtered.length})`} onClear={() => setOverdueOnly(false)} testid="actions-overdue-filter" />
      )}

      {/* Filters */}
      <Card className="p-4 bg-white">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-slate-400" />
          <Input placeholder="Search by Action ID, title, finding, owner..." value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-md" data-testid="action-search" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="rounded-sm border border-slate-300 bg-white text-slate-900 px-2 py-1.5 text-xs" data-testid="filter-status">
            <option value="">All Status</option>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} className="rounded-sm border border-slate-300 bg-white text-slate-900 px-2 py-1.5 text-xs" data-testid="filter-priority">
            <option value="">All Priority</option>
            {PRIORITIES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="rounded-sm border border-slate-300 bg-white text-slate-900 px-2 py-1.5 text-xs" data-testid="filter-type">
            <option value="">All Types</option>
            {TYPES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="rounded-sm border border-slate-300 bg-white text-slate-900 px-2 py-1.5 text-xs" data-testid="filter-source">
            <option value="">All Sources</option>
            {SOURCES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={filterOwner} onChange={(e) => setFilterOwner(e.target.value)} className="rounded-sm border border-slate-300 bg-white text-slate-900 px-2 py-1.5 text-xs" data-testid="filter-owner">
            <option value="">All Owners</option>
            {owners.map((o) => <option key={o}>{o}</option>)}
          </select>
          <select value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)} className="rounded-sm border border-slate-300 bg-white text-slate-900 px-2 py-1.5 text-xs" data-testid="filter-department">
            <option value="">All Departments</option>
            {departments.map((d) => <option key={d}>{d}</option>)}
          </select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="text-xs" title="Due from" data-testid="filter-date-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="text-xs" title="Due to" data-testid="filter-date-to" />
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {[
                  ['action_id', 'Action ID'], ['title', 'Title'], ['type', 'Type'],
                  ['source', 'Source'], ['finding', 'Finding / Observation'],
                  ['priority', 'Priority'], ['action_owner', 'Action Owner'],
                  ['date_due', 'Date Due'], ['status', 'Status'],
                ].map(([k, lbl]) => (
                  <th key={k}
                      className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase cursor-pointer select-none"
                      onClick={() => { setSortDir(sortKey === k && sortDir === 'asc' ? 'desc' : 'asc'); setSortKey(k); }}>
                    {lbl} {sortKey === k ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                  </th>
                ))}
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {pagedRows.map((a, idx) => (
                <tr key={a.id} className={`hover:bg-slate-50 ${isOverdue(a) ? 'bg-rose-50/40' : ''}`} data-testid={`action-row-${idx}`}>
                  <td className="px-3 py-2 font-mono text-xs">{a.action_id}</td>
                  <td className="px-3 py-2 max-w-xs truncate text-slate-900">{a.title}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{a.type}</td>
                  <td className="px-3 py-2 text-xs text-slate-600">{a.source}</td>
                  <td className="px-3 py-2 max-w-sm truncate text-xs text-slate-600">{a.finding}</td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${priorityColor(a.priority)}`}>{a.priority}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-700">{a.action_owner}</td>
                  <td className="px-3 py-2 text-xs text-slate-700 whitespace-nowrap">
                    {a.date_due}
                    {isOverdue(a) && <span className="ml-1 text-rose-600">●</span>}
                  </td>
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${statusColor(a.status)}`}>{a.status}</span></td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setViewingAction(a)} className="px-2 py-1" data-testid={`view-action-${idx}`}><Eye className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => openEdit(a)} className="px-2 py-1" data-testid={`edit-action-${idx}`}><Edit className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(a)} className="px-2 py-1" data-testid={`delete-action-${idx}`}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {pagedRows.length === 0 && (
                <tr><td colSpan={10} className="text-center py-10 text-sm text-slate-500">No actions match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-t text-xs text-slate-600">
          <span data-testid="paging-count">{sorted.length} action{sorted.length === 1 ? '' : 's'} · page {page} of {totalPages}</span>
          <div className="flex gap-1">
            <button onClick={() => setPage(1)} disabled={page === 1} className="p-1 disabled:opacity-40"><ChevronsLeft className="w-4 h-4" /></button>
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-1 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-1 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="p-1 disabled:opacity-40"><ChevronsRight className="w-4 h-4" /></button>
          </div>
        </div>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={(o) => !o && setShowForm(false)}>
        <DialogContent className="sm:max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAction ? `Edit ${editingAction.action_id}` : 'Add New Action'}</DialogTitle>
            <DialogDescription>Capture improvement, corrective, audit or remediation actions.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-6">
            {/* Identification */}
            <section>
              <h3 className="text-sm font-semibold text-slate-800 mb-2 border-b pb-1">Identification</h3>
              <div className="grid grid-cols-2 gap-3">
                {editingAction && (
                  <div className="col-span-2 bg-slate-50 rounded p-2 text-xs text-slate-600">
                    <strong>Action ID:</strong> {editingAction.action_id} <span className="text-slate-400">(system-generated)</span>
                  </div>
                )}
                <div className="col-span-2">
                  <Label>Title *</Label>
                  <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} data-testid="form-title" required />
                </div>
                <div>
                  <Label>Type *</Label>
                  <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full rounded-sm border px-3 py-2 text-sm" data-testid="form-type">
                    {TYPES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Status *</Label>
                  <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full rounded-sm border px-3 py-2 text-sm" data-testid="form-status">
                    {STATUSES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Priority *</Label>
                  <select value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} className="w-full rounded-sm border px-3 py-2 text-sm" data-testid="form-priority">
                    {PRIORITIES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* Source */}
            <section>
              <h3 className="text-sm font-semibold text-slate-800 mb-2 border-b pb-1">Source</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Source *</Label>
                  <select value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })} className="w-full rounded-sm border px-3 py-2 text-sm" data-testid="form-source">
                    {SOURCES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Date Identified *</Label>
                  <Input type="date" value={formData.date_identified} onChange={(e) => setFormData({ ...formData, date_identified: e.target.value })} data-testid="form-date-identified" required />
                </div>
              </div>
            </section>

            {/* Finding / Issue */}
            <section>
              <h3 className="text-sm font-semibold text-slate-800 mb-2 border-b pb-1">Finding / Issue</h3>
              <div className="space-y-3">
                <div>
                  <Label>Finding / Observation *</Label>
                  <Textarea rows={3} value={formData.finding} onChange={(e) => setFormData({ ...formData, finding: e.target.value })} data-testid="form-finding" required />
                </div>
                <div>
                  <Label>Classification *</Label>
                  <select value={formData.classification} onChange={(e) => setFormData({ ...formData, classification: e.target.value })} className="w-full rounded-sm border px-3 py-2 text-sm" data-testid="form-classification">
                    {CLASSIFICATIONS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* Ownership */}
            <section>
              <h3 className="text-sm font-semibold text-slate-800 mb-2 border-b pb-1">Ownership</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Department</Label>
                  <Input value={formData.department} onChange={(e) => setFormData({ ...formData, department: e.target.value })} placeholder="e.g. Information Security" data-testid="form-department" />
                </div>
                <div>
                  <Label>Raised By</Label>
                  <Input value={formData.raised_by} onChange={(e) => setFormData({ ...formData, raised_by: e.target.value })} list="tenant-users-list" data-testid="form-raised-by" />
                  <datalist id="tenant-users-list">
                    {tenantUsers.map((u) => <option key={u.id} value={u.email}>{u.name || u.email}</option>)}
                  </datalist>
                </div>
                <div>
                  <Label>Action Owner *</Label>
                  <Input value={formData.action_owner} onChange={(e) => setFormData({ ...formData, action_owner: e.target.value })} list="tenant-users-list" data-testid="form-action-owner" required />
                </div>
                <div>
                  <Label>Date Due *</Label>
                  <Input type="date" value={formData.date_due} onChange={(e) => setFormData({ ...formData, date_due: e.target.value })} data-testid="form-date-due" required />
                </div>
                <div>
                  <Label>Frequency *</Label>
                  <select value={formData.frequency} onChange={(e) => setFormData({ ...formData, frequency: e.target.value })} className="w-full rounded-sm border px-3 py-2 text-sm" data-testid="form-frequency" required>
                    {FREQUENCIES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* Root Cause & Plan */}
            <section>
              <h3 className="text-sm font-semibold text-slate-800 mb-2 border-b pb-1">Root Cause &amp; Action</h3>
              <div className="space-y-3">
                <div>
                  <Label>Root Cause</Label>
                  <Textarea rows={2} value={formData.root_cause} onChange={(e) => setFormData({ ...formData, root_cause: e.target.value })} data-testid="form-root-cause" />
                </div>
                <div>
                  <Label>Root Cause Category</Label>
                  <select value={formData.root_cause_category} onChange={(e) => setFormData({ ...formData, root_cause_category: e.target.value })} className="w-full rounded-sm border px-3 py-2 text-sm" data-testid="form-rc-category">
                    <option value="">—</option>
                    {ROOT_CAUSE_CATEGORIES.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Action Plan *</Label>
                  <Textarea rows={3} value={formData.action_plan} onChange={(e) => setFormData({ ...formData, action_plan: e.target.value })} data-testid="form-action-plan" required />
                </div>
              </div>
            </section>

            {/* Closure */}
            <section>
              <h3 className="text-sm font-semibold text-slate-800 mb-2 border-b pb-1">Closure</h3>
              <p className="text-xs text-slate-500 mb-2">Required fields apply once the action moves to Closed.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Completion Date {formData.status === 'Closed' && '*'}</Label>
                  <Input type="date" value={formData.completion_date} onChange={(e) => setFormData({ ...formData, completion_date: e.target.value })} data-testid="form-completion-date" />
                </div>
                <div>
                  <Label>Effectiveness Check</Label>
                  <select value={formData.effectiveness_check} onChange={(e) => setFormData({ ...formData, effectiveness_check: e.target.value })} className="w-full rounded-sm border px-3 py-2 text-sm" data-testid="form-effectiveness">
                    <option value="">—</option>
                    {EFFECTIVENESS.map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Effectiveness Review Date</Label>
                  <Input type="date" value={formData.effectiveness_review_date} onChange={(e) => setFormData({ ...formData, effectiveness_review_date: e.target.value })} data-testid="form-eff-review-date" />
                </div>
                <div>
                  <Label>Closure Comments</Label>
                  <Input value={formData.closure_comments} onChange={(e) => setFormData({ ...formData, closure_comments: e.target.value })} data-testid="form-closure-comments" />
                </div>
                <div className="col-span-2">
                  <Label>Outcome / Resolution {formData.status === 'Closed' && '*'}</Label>
                  <Textarea rows={2} value={formData.outcome} onChange={(e) => setFormData({ ...formData, outcome: e.target.value })} data-testid="form-outcome" />
                </div>
              </div>
            </section>

            {editingAction && (
              <section>
                <h3 className="text-sm font-semibold text-slate-800 mb-2 border-b pb-1">System</h3>
                <p className="text-xs text-slate-500 mb-2">These fields are managed automatically and can't be edited.</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <Label className="text-slate-500">Closed By</Label>
                    <p className="bg-slate-50 border rounded px-3 py-2 text-slate-900 min-h-[38px]" data-testid="sys-closed-by">
                      {editingAction.closed_by || '—'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Created Date</Label>
                    <p className="bg-slate-50 border rounded px-3 py-2 text-slate-900 min-h-[38px]" data-testid="sys-created-date">
                      {(editingAction.created_date || '').slice(0, 10) || '—'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Created By</Label>
                    <p className="bg-slate-50 border rounded px-3 py-2 text-slate-900 min-h-[38px]" data-testid="sys-created-by">
                      {editingAction.created_by || '—'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Last Updated</Label>
                    <p className="bg-slate-50 border rounded px-3 py-2 text-slate-900 min-h-[38px]" data-testid="sys-updated">
                      {(editingAction.last_updated || '').slice(0, 16).replace('T', ' ') || '—'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Last Updated By</Label>
                    <p className="bg-slate-50 border rounded px-3 py-2 text-slate-900 min-h-[38px]" data-testid="sys-updated-by">
                      {editingAction.last_updated_by || '—'}
                    </p>
                  </div>
                </div>
              </section>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="save-action-btn">
                {saving ? 'Saving...' : (editingAction ? 'Update Action' : 'Create Action')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View / Evidence Dialog */}
      <Dialog open={!!viewingAction} onOpenChange={(o) => !o && setViewingAction(null)}>
        <DialogContent className="sm:max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          {viewingAction && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="font-mono text-xs bg-slate-100 rounded px-2 py-1">{viewingAction.action_id}</span>
                  <span>{viewingAction.title}</span>
                </DialogTitle>
                <DialogDescription>
                  <span className={`inline-block px-2 py-0.5 rounded-sm text-xs mr-1 ${statusColor(viewingAction.status)}`}>{viewingAction.status}</span>
                  <span className={`inline-block px-2 py-0.5 rounded-sm text-xs mr-1 ${priorityColor(viewingAction.priority)}`}>{viewingAction.priority}</span>
                  <span className="text-xs text-slate-600">{viewingAction.type} · {viewingAction.source}</span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <Field label="Finding / Observation">{viewingAction.finding}</Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Classification">{viewingAction.classification}</Field>
                  <Field label="Department">{viewingAction.department || '—'}</Field>
                  <Field label="Raised By">{viewingAction.raised_by}</Field>
                  <Field label="Action Owner">{viewingAction.action_owner}</Field>
                  <Field label="Date Identified">{viewingAction.date_identified}</Field>
                  <Field label="Date Due">{viewingAction.date_due}</Field>
                  <Field label="Frequency">{viewingAction.frequency || 'One-off'}</Field>
                </div>
                <Field label="Root Cause">{viewingAction.root_cause || '—'}</Field>
                <Field label="Root Cause Category">{viewingAction.root_cause_category || '—'}</Field>
                <Field label="Action Plan">{viewingAction.action_plan}</Field>

                {/* Evidence */}
                <section className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-slate-800">Evidence</h3>
                    <label className="inline-flex items-center gap-1 text-xs text-slate-700 bg-slate-100 hover:bg-slate-200 rounded px-3 py-1.5 cursor-pointer">
                      <Paperclip className="w-3 h-3" /> Upload
                      <input type="file" className="hidden" accept=".doc,.docx,.pdf,.jpg,.jpeg,.png,.ppt,.pptx"
                             onChange={(e) => { if (e.target.files?.[0]) { uploadEvidence(e.target.files[0]); e.target.value = ''; } }}
                             data-testid="upload-evidence" />
                    </label>
                  </div>
                  {(viewingAction.evidence || []).length === 0 && (
                    <p className="text-xs text-slate-500">No evidence attached.</p>
                  )}
                  <ul className="divide-y border rounded">
                    {(viewingAction.evidence || []).map((ev) => (
                      <li key={ev.id} className="flex items-center gap-2 p-2 text-xs">
                        <Paperclip className="w-3 h-3 text-slate-400" />
                        <span className="font-medium text-slate-900 flex-1 truncate">{ev.filename}</span>
                        <span className="text-slate-500">{(ev.size / 1024).toFixed(1)} KB</span>
                        <span className="text-slate-500">{(ev.uploaded_at || '').slice(0, 10)}</span>
                        <span className="text-slate-500">{ev.uploaded_by}</span>
                        <button onClick={() => downloadEvidence(ev)} className="text-slate-600 hover:text-slate-900"><Download className="w-3 h-3" /></button>
                        <button onClick={() => deleteEvidence(ev)} className="text-rose-600 hover:text-rose-800"><Trash2 className="w-3 h-3" /></button>
                      </li>
                    ))}
                  </ul>
                </section>

                {(viewingAction.status === 'Completed' || viewingAction.status === 'Closed') && (
                  <section className="border-t pt-3">
                    <h3 className="text-sm font-semibold text-slate-800 mb-2">Closure</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Completion Date">{viewingAction.completion_date || '—'}</Field>
                      <Field label="Effectiveness Check">{viewingAction.effectiveness_check || '—'}</Field>
                      <Field label="Effectiveness Review Date">{viewingAction.effectiveness_review_date || '—'}</Field>
                      <Field label="Closed By">{viewingAction.closed_by || '—'}</Field>
                    </div>
                    <Field label="Outcome / Resolution">{viewingAction.outcome || '—'}</Field>
                    <Field label="Closure Comments">{viewingAction.closure_comments || '—'}</Field>
                  </section>
                )}

                <section className="border-t pt-3 text-xs text-slate-500">
                  Created by {viewingAction.created_by} on {(viewingAction.created_date || '').slice(0, 10)}.
                  {viewingAction.last_updated && (
                    <> Last updated by {viewingAction.last_updated_by} on {viewingAction.last_updated.slice(0, 10)}.</>
                  )}
                </section>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={(o) => !o && setShowImport(false)}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>Import Actions</DialogTitle>
            <DialogDescription>Upload a filled copy of the Action Register template.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <input type="file" accept=".xlsx" onChange={(e) => setImportFile(e.target.files?.[0] || null)} data-testid="import-file" />
            {importResult && (
              <div className="text-xs bg-slate-50 border rounded p-2">
                <p><strong>Imported:</strong> {importResult.imported} · <strong>Skipped:</strong> {importResult.skipped}</p>
                {importResult.errors?.length > 0 && (
                  <ul className="mt-1 text-rose-600 space-y-0.5">
                    {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowImport(false)}>Close</Button>
              <Button onClick={doImport} disabled={!importFile || importing} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="confirm-import">
                {importing ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-slate-900 whitespace-pre-wrap">{children}</p>
    </div>
  );
}
